import { combineReducers, configureStore, Dispatch, UnknownAction } from "@reduxjs/toolkit";

import { useDispatch } from "react-redux";
import localStorage from "redux-persist/es/storage";
import sessionStorage from "redux-persist/es/storage/session";

import { ThunkDispatch } from "redux-thunk";
import { setupListeners } from "@reduxjs/toolkit/query";

import {
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
    createMigrate,
    persistStore,
} from "redux-persist";

import sessionReducer from "./slices/sessionSlice";
import componentsReducer from "./slices/componentsSlice";

import { baseApi } from "@services/api/baseApi";
// import { migrations } from "./migrations/migrations";
import { localStorageMigrations } from "./migrations/localStorageMigrations";
import { sessionStorageMigrations } from "./migrations/sessionStorageMigrations";

const localPersistConfig = {
    key: "lendia_bpm-local",
    storage: localStorage,
    version: 0,
    migrate: createMigrate(localStorageMigrations, { debug: true }),
    blacklist: [baseApi.reducerPath],
};

const sessionPersistConfig = {
    key: "lendia_bpm-session",
    storage: sessionStorage,
    version: 0,
    migrate: createMigrate(sessionStorageMigrations, { debug: true }),
    blacklist: [baseApi.reducerPath],
};

const rootReducer = combineReducers({
    session: persistReducer(localPersistConfig, sessionReducer),
    components: persistReducer(sessionPersistConfig, componentsReducer),
    [baseApi.reducerPath]: baseApi.reducer,
});

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                    "components",
                    "components/setDialogConfig",
                    "components/setPopupConfig",
                    "components/setVisibleButtons",
                ],
                ignoredActionPaths: ["components", "meta.arg", "meta.baseQueryMeta"],
                ignoredPaths: ["components"],
            },
        })
            .concat(baseApi.middleware)
            .concat((storeAPI) => (next) => (action) => {
                if (action.type === "CLEAN_STORE" || action.type === "USER_LOGOUT") {
                    localPersistor.flush().then(() => localPersistor.purge());

                    sessionPersistor.flush().then(() => sessionPersistor.purge());

                    storeAPI.dispatch({ type: "session/resetSession" });
                    storeAPI.dispatch({ type: "components/resetComponents" });
                }

                return next(action);
            }),
});

export const localPersistor = persistStore(store, { key: "lendia_bpm-local" });
export const sessionPersistor = persistStore(store, { key: "lendia_bpm-session" });

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;

type AppDispatch = ThunkDispatch<any, undefined, UnknownAction> & Dispatch<any>;
export const useAppDispatch: () => AppDispatch = useDispatch;
